// frontend/src/content/friendCode/copy.js
//
// THE ONE copy table for every customer-facing friend-code ("קוד חבר" /
// "הטבת חבר") string - sign-up field, checkout block, offer surfaces (navbar
// chip, dashboard banner, checkout price block) and the Billing tab lines.
//
// STATUS OF THE WORDING
//   * Every HEBREW string is the owner's approved copy (sheet of 2026-10-03,
//     plus the 2026-10-04 approvals: `checkout_in_progress`, the one-charge
//     pair, `not_eligible` and the price-loading line), used VERBATIM
//     (scratchpad `friend-code-approved-copy.md`). Never reword one here - a
//     change goes back to the owner first.
//   * Hebrew keys listed in `PENDING_HEBREW_KEYS` (none today) are NOT yet
//     approved.
//   * Every ENGLISH string is a translation twin for the English UI mode and is
//     PENDING owner approval as a whole (the sheet is Hebrew only).
//
// COPY RULES (CLAUDE.md): ASCII hyphen only, no emojis, numbers come from the
// API only - this table never computes a price or a percent, it only places
// values a caller already has into an approved sentence.
//
// SHAPE. Every entry is a TEMPLATE string with `{token}` fields, per language.
// `fillTemplate` renders the whole sentence; `templateParts` splits it so a
// component can style one token (the struck full price) without rewording or
// re-ordering anything around it.

/** Hebrew keys whose wording the owner has not approved yet (none today -
 * every Hebrew string below was approved by 2026-10-04). */
const PENDING_HEBREW_KEYS = [];

/**
 * The templates. `he` is the approved sheet; `en` is the pending twin.
 * Keys are stable ids - tests and the report reference them.
 */
export const FRIEND_CODE_TEMPLATES = Object.freeze({
  // ── Register page (email + Google sign-up) ──────────────────────────────
  "register.toggle": { he: "יש לך קוד חבר?", en: "Have a friend code?" },
  "register.label": { he: "קוד חבר", en: "Friend code" },
  "register.helper": {
    he: "קיבלתם קוד חבר? הזינו אותו והטבת החבר תופעל אחרי ההרשמה.",
    en: "Got a friend code? Enter it and the friend benefit will be applied after you sign up.",
  },
  "register.autoFilled": {
    he: "קוד החבר הוזן אוטומטית",
    en: "The friend code was filled in automatically",
  },
  "register.valid": {
    he: "קוד החבר תקין - {percent}% הנחה על החודשיים הראשונים",
    en: "Valid friend code - {percent}% off your first two months",
  },
  "register.invalid": {
    he: "הקוד לא תקין, כדאי לבדוק אותו",
    en: "This code is not valid, please check it",
  },
  "register.throttled": {
    he: "היו הרבה ניסיונות שגויים. אפשר לנסות שוב בעוד כמה שניות.",
    en: "Too many wrong attempts. You can try again in a few seconds.",
  },

  // ── Checkout code entry ─────────────────────────────────────────────────
  "checkout.prompt": { he: "יש לך קוד חבר?", en: "Have a friend code?" },
  "checkout.label": { he: "קוד חבר", en: "Friend code" },
  "checkout.apply": { he: "הפעלה", en: "Apply" },
  "checkout.success": {
    he: "קוד החבר הופעל - {percent}% הנחה על החודשיים הראשונים.",
    en: "Friend code applied - {percent}% off your first two months.",
  },
  "checkout.refusal.invalid_code": {
    he: "הקוד לא תקין. כדאי לבדוק אותו ולנסות שוב.",
    en: "This code is not valid. Please check it and try again.",
  },
  "checkout.refusal.already_attributed": {
    he: "כבר הופעל קוד חבר בחשבון הזה.",
    en: "A friend code is already active on this account.",
  },
  "checkout.refusal.not_eligible_after_payment": {
    he: "אפשר להזין קוד חבר רק לפני התשלום הראשון.",
    en: "A friend code can only be entered before the first payment.",
  },
  "checkout.refusal.too_many_attempts": {
    he: "היו הרבה ניסיונות שגויים. אפשר לנסות שוב בעוד כמה שניות.",
    en: "Too many wrong attempts. You can try again in a few seconds.",
  },
  // Approved 2026-10-04.
  "checkout.refusal.checkout_in_progress": {
    he: "יש תשלום בתהליך. אפשר להזין קוד חבר בעוד כמה דקות.",
    en: "A payment is in progress. You can enter a friend code in a few minutes.",
  },
  // Approved 2026-10-04 (F4): a LIVE code that does not apply to this account
  // (admin / invoice-billed / a returning deleted email / an account that
  // predates the partner). Calling it "invalid" misled a legitimate customer.
  "checkout.refusal.not_eligible": {
    he: "קוד החבר לא מתאים לחשבון הזה.",
    en: "This friend code does not apply to this account.",
  },
  // Not friend-code copy: the app's EXISTING, shipped generic failure sentence
  // (Register.js `registrationFailed`), reused for a server fault so no new
  // Hebrew is invented for it.
  "checkout.refusal.error": {
    he: "אירעה שגיאה. אנא נסה שוב.",
    en: "Something went wrong. Please try again.",
  },

  // Approved 2026-10-04 (F3): the checkout could not read a REFERRED
  // customer's price yet (a slow or failed offer request). The plan buttons
  // stay held while this shows - a referred customer never pays a price that
  // is not on screen.
  "checkout.priceLoading": { he: "טוענים את המחיר...", en: "Loading the price..." },
  // Not friend-code copy: the app's EXISTING, shipped customer-facing retry
  // label (`components/payment/paymentCopy.js` TRY_AGAIN, settings
  // `billingText.retry`), reused for the manual retry once the automatic price
  // retries stop (L-03) - shown
  // beside `checkout.refusal.error`, so no new Hebrew is invented for it.
  "checkout.priceRetry": { he: "נסה שוב", en: "Try again" },

  // ── Offer surfaces (friend-code customers only) ─────────────────────────
  "offer.navbarChip": { he: "{percent}% הנחת חבר", en: "{percent}% friend discount" },
  "offer.banner": {
    he: "הטבת חבר: {percent}% הנחה על החודשיים הראשונים.",
    en: "Friend benefit: {percent}% off your first two months.",
  },
  "offer.bannerCta": { he: "לבחירת מסלול", en: "Choose a plan" },
  "offer.priceHeader": { he: "הטבת חבר - {percent}% הנחה", en: "Friend benefit - {percent}% off" },
  // Approved 2026-10-04 - two discounted charges left: ONE bold line for both
  // months (it replaced the "first month" / "second month too" pair), then the
  // regular price from the third month.
  "offer.priceFirstTwoMonths": {
    he: "החודש הראשון + החודש השני ב-{price} ₪ כל אחד {struck}",
    en: "First and second month: {price} ₪ each {struck}",
  },
  "offer.priceFromThirdMonth": {
    he: "מהחודש השלישי: {full} ₪ לחודש.",
    en: "From the third month: {full} ₪ per month.",
  },
  // Approved 2026-10-04 - one discounted charge left (an upgrade after the
  // first paid month). The sheet's one sentence, split at its own full stop.
  "offer.priceThisCharge": {
    he: "החיוב הזה: {price} ₪ {struck}.",
    en: "This charge: {price} ₪ {struck}.",
  },
  "offer.priceFromNextCharge": {
    he: "מהחיוב הבא: {full} ₪ לחודש.",
    en: "From the next charge: {full} ₪ per month.",
  },
  // The struck full price's struck token itself.
  "offer.struckAmount": { he: "{full} ₪", en: "{full} ₪" },

  // ── Billing tab (Settings) ──────────────────────────────────────────────
  // Approved 2026-10-04: the referral amount and the next billing date as ONE
  // line ("החיוב הבא: 159 ₪ ב-18 באוק׳ (הטבת חבר - 20% הנחה)"), replacing
  // the separate "חיוב הבא: <date>" line. {date} is day + short month, no
  // year (`formatDayMonth` in billing/subscriptionView.js).
  "billing.nextChargeOn": {
    he: "החיוב הבא: {price} ₪ ב-{date} (הטבת חבר - {percent}% הנחה)",
    en: "Next charge: {price} ₪ on {date} (friend benefit - {percent}% off)",
  },
  // The same sentence without a date - only when no date may be printed (no
  // period end on the row, or a renewal that is overdue, whose date already
  // went by).
  "billing.nextCharge": {
    he: "החיוב הבא: {price} ₪ (הטבת חבר - {percent}% הנחה)",
    en: "Next charge: {price} ₪ (friend benefit - {percent}% off)",
  },
  "billing.followingCharge": {
    he: "מהחיוב שאחריו: {full} ₪ לחודש.",
    en: "From the charge after that: {full} ₪ per month.",
  },
});

/**
 * EVERY string not yet owner-approved: the Hebrew keys above, plus the
 * English twin of EVERY key as `en:<key>` (the approved sheet is Hebrew
 * only, so no English string is approved).
 */
export const PENDING_OWNER_APPROVAL = Object.freeze([
  ...PENDING_HEBREW_KEYS,
  ...Object.keys(FRIEND_CODE_TEMPLATES).map((key) => `en:${key}`),
]);

const TOKEN_RE = /\{(\w+)\}/g;

/**
 * The raw template for one key in one language.
 *
 * Falls back to the HEBREW template when a key or language is missing - the
 * approved wording is the one that exists for certain - and to "" for an
 * unknown key, so a typo can never render "undefined" on a customer surface.
 *
 * @param {string} key
 * @param {boolean} isHebrew
 * @returns {string}
 */
export const friendCodeTemplate = (key, isHebrew = true) => {
  const entry = FRIEND_CODE_TEMPLATES[key];
  if (!entry) return "";
  return (isHebrew ? entry.he : entry.en) || entry.he || "";
};

/**
 * The template with every `{token}` replaced by its value.
 *
 * A token with no value renders as "" (never "undefined"), but callers are
 * expected to pass every token - a surface with a missing number does not
 * render the sentence at all.
 *
 * @param {string} key
 * @param {boolean} isHebrew
 * @param {Object<string, string|number>} [values]
 * @returns {string}
 */
export const fillTemplate = (key, isHebrew, values = {}) =>
  friendCodeTemplate(key, isHebrew).replace(TOKEN_RE, (_, name) =>
    values[name] === undefined || values[name] === null ? "" : String(values[name])
  );

/**
 * The template as ordered segments: `{text}` runs and `{token}` slots.
 *
 * Lets a component render one token with its own element (a struck price)
 * while every word around it stays exactly the approved text, in order.
 *
 * @param {string} key
 * @param {boolean} isHebrew
 * @param {Object<string, string|number>} [values] filled into plain tokens;
 *   a token listed in `slots` is returned as `{slot: name}` instead.
 * @param {string[]} [slots]
 * @returns {Array<{text: string}|{slot: string}>}
 */
export const templateParts = (key, isHebrew, values = {}, slots = []) => {
  const template = friendCodeTemplate(key, isHebrew);
  const parts = [];
  let last = 0;
  template.replace(TOKEN_RE, (match, name, offset) => {
    if (offset > last) parts.push({ text: template.slice(last, offset) });
    if (slots.includes(name)) {
      parts.push({ slot: name });
    } else {
      const value = values[name];
      parts.push({ text: value === undefined || value === null ? "" : String(value) });
    }
    last = offset + match.length;
    return match;
  });
  if (last < template.length) parts.push({ text: template.slice(last) });
  // Merge adjacent text runs so a consumer sees the fewest nodes.
  return parts.reduce((merged, part) => {
    const previous = merged[merged.length - 1];
    if (previous && "text" in previous && "text" in part) {
      previous.text += part.text;
    } else {
      merged.push({ ...part });
    }
    return merged;
  }, []);
};

/**
 * The checkout refusal sentence for a server token (409 detail, 429 detail,
 * from `POST /users/me/partner-code`). Byte-exact token matching, never substring.
 *
 * `not_eligible` (admin / invoice-billed / a returning deleted email / an
 * account that predates the partner) has its own approved sentence ("does not
 * apply to this account") - it never says WHY. Anything unknown reads as the
 * generic failure.
 *
 * @param {string} token
 * @param {boolean} isHebrew
 * @returns {string}
 */
export const checkoutRefusalText = (token, isHebrew) => {
  const key = `checkout.refusal.${token}`;
  return FRIEND_CODE_TEMPLATES[key]
    ? fillTemplate(key, isHebrew)
    : fillTemplate("checkout.refusal.error", isHebrew);
};
